import React, { useState } from "react";
import Modal from "./Modal";
import MobileButton from "../../Mobile/Buttons/MobileButton";
import "./Modal.css";

/**
 * ConfirmModal - Modal for user confirmations with actions
 * 
 * @param {boolean} isOpen - Controls modal visibility
 * @param {function} onClose - Callback when modal closes
 * @param {string} title - Modal title
 * @param {string|React.ReactNode} description - Modal description/message
 * @param {object} primaryAction - Primary action button config { label, onClick, variant, bg, icon }
 * @param {object} secondaryAction - Secondary action button config { label, onClick, icon }
 * @param {React.ReactNode} icon - Custom icon element
 * @param {string} iconType - Predefined icon type: 'info' | 'warning' | 'error' | 'success'
 * @param {string} footerNote - Optional footer note (e.g., security message)
 * @param {string} size - Modal size: 'sm' | 'md' | 'lg'
 */
const ConfirmModal = ({
    isOpen,
    onClose,
    title,
    description,
    primaryAction,
    secondaryAction,
    icon,
    iconType,
    footerNote,
    size = "sm",
    canClose = true,
    isLoading = false,
    children,
}) => {
    const [internalLoading, setInternalLoading] = useState(false);
    const isMounted = React.useRef(true);

    const loading = isLoading || internalLoading;

    React.useEffect(() => {
        return () => {
            isMounted.current = false;
        };
    }, []);

    const handlePrimaryClick = async (e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
            if (e.nativeEvent) {
                e.nativeEvent.stopImmediatePropagation();
            }
        }
        if (!primaryAction?.onClick) return;

        try {
            if (isMounted.current) setInternalLoading(true);
            await primaryAction.onClick();
        } catch (error) {
            console.error("Error in primary action:", error);
        } finally {
            if (isMounted.current) setInternalLoading(false);
        }
    };

    const handleSecondaryClick = async (e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
            if (e.nativeEvent) {
                e.nativeEvent.stopImmediatePropagation();
            }
        }
        if (!secondaryAction?.onClick) return;

        try {
            await secondaryAction.onClick();
        } catch (error) {
            console.error("Error in secondary action:", error);
        }
    };

    // Icon mapping - More premium minimalist sizes
    const iconMap = {
        info: (
            <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 60 60" fill="none">
                <circle cx="30" cy="30" r="28" fill="var(--elevva-info-bg)" />
                <path d="M30 22V24M30 28V38" stroke="var(--elevva-info)" strokeWidth="2.5" strokeLinecap="round" />
                <circle cx="30" cy="23" r="1.5" fill="var(--elevva-info)" />
            </svg>
        ),
        warning: (
            <svg xmlns="http://www.w3.org/2000/svg" width="72" height="72" viewBox="0 0 60 60" fill="none">
                <circle cx="30" cy="30" r="28" fill="var(--elevva-warning-bg)" />
                <circle cx="30" cy="30" r="14" fill="var(--elevva-warning-bg)" />
                <path d="M30 24V32" stroke="var(--elevva-warning)" strokeWidth="2.5" strokeLinecap="round" />
                <rect x="29" y="35" width="2" height="2" rx="1" fill="var(--elevva-warning)" />
                <circle cx="30" cy="30" r="18" stroke="var(--elevva-warning)" strokeWidth="0.5" strokeDasharray="2 2" opacity="0.5" />
            </svg>
        ),
        error: (
            <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 60 60" fill="none">
                <circle cx="30" cy="30" r="28" fill="var(--elevva-error-bg)" />
                <path d="M24 24L36 36M36 24L24 36" stroke="var(--elevva-error)" strokeWidth="3" strokeLinecap="round" />
            </svg>
        ),
        success: (
            <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 60 60" fill="none">
                <circle cx="30" cy="30" r="28" fill="var(--elevva-success-bg)" />
                <path d="M22 30L28 36L40 24" stroke="var(--elevva-success)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
        ),
    };

    const displayIcon = icon || (iconType && iconMap[iconType]);

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            size={size}
            closeOnOverlayClick={canClose}
            closeOnEsc={canClose}
        >
            <div className="modal-content animate-fade-in">
                {displayIcon && (
                    <div className="modal-icon-container mb-1">{displayIcon}</div>
                )}

                {title && <h2 className="modal-title mb-1" style={{ fontSize: '14px', letterSpacing: '-0.01em' }}>{title}</h2>}

                {description && (
                    <div className="modal-description mb-3" style={{ color: '#64748B', fontSize: '12px', whiteSpace: 'pre-line' }}>
                        {description}
                    </div>
                )}

                {children && (
                    <div className="modal-extra-content">
                        {children}
                    </div>
                )}

                <div className="modal-actions">
                    {primaryAction && (
                        <MobileButton
                            text={primaryAction.label}
                            onClick={handlePrimaryClick}
                            loading={loading}
                            variant="primary"
                            bg={
                                (primaryAction.bg === 'danger' || primaryAction.variant === 'danger')
                                    ? 'var(--elevva-error)'
                                    : (!primaryAction.bg || primaryAction.bg === 'primary')
                                        ? 'var(--elevva-blue)'
                                        : primaryAction.bg
                            }
                            icon={primaryAction.icon}
                        />
                    )}

                    {secondaryAction && (
                        <MobileButton
                            text={secondaryAction.label}
                            onClick={handleSecondaryClick}
                            disabled={loading}
                            variant="secondary"
                            icon={secondaryAction.icon}
                        />
                    )}
                </div>

                {footerNote && (
                    <div className="modal-footer-note mt-4">
                        <i className="bi bi-shield-lock-fill" />
                        {footerNote}
                    </div>
                )}
            </div>
        </Modal>
    );
};

export default ConfirmModal;
